import React, { useState, useEffect } from 'react';
import api from '../../../api';
import { pouchIcon, mineLureImg, dropLureImg, lureImg, superLureImg, ultraLureImg, rubyGummyImg, sapphireGummyImg, emeraldGummyImg, goldenGummyImg, diamondGummyImg } from '../../../constants/items';
import './BagItemsCard.css';

const BagItemsCard = ({ 
  mineralPotionActive, 
  mineralPotionCountdown,
  shinyPotionActive,
  shinyPotionCountdown,
  shinyCharm,
  shinySkillBonus,
  activeLure,
  countdown: lureCountdown,
  expLureHits,
  dropLureHits,
  onOpenBag,
  onActiveGummyChange,
  spawnText,
  playerPos,
  equippedRings = [],
  onOpenHeldItemSwap,
  onOpenDepartmentStore
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [repelActive, setRepelActive] = useState(false);
  const [repelCountdown, setRepelCountdown] = useState(0);
  const [ringPotionActive, setRingPotionActive] = useState(false);
  const [ringPotionCountdown, setRingPotionCountdown] = useState(0);
  const [scentgHits, setScentgHits] = useState(0);
  const [activeGummy, setActiveGummy] = useState(null);
  const [gummyCountdown, setGummyCountdown] = useState(0);
  const [hasRingOfHealth, setHasRingOfHealth] = useState(false);
  const [ringOnCooldown, setRingOnCooldown] = useState(false);
  const [ringCooldownRemaining, setRingCooldownRemaining] = useState(0);
  const [healingParty, setHealingParty] = useState(false);

  // Check for lure status (includes scentg, repel, ring potion data)
  useEffect(() => {
    const checkItemStatus = async () => {
      try {
        // Check lure status for scentg (Scent of Gengar)
        const lureResponse = await api.get("/v1/lure-status", { withCredentials: true });
        
        // Set Scent of Gengar attempts
        setScentgHits(lureResponse.data.scentg || 0);
        
        // Check gummy status
        const gummyResponse = await api.get("/v1/gummy-status", { withCredentials: true });
        const gummyData = gummyResponse.data;
        
        // Find which gummy is active (has value > 0)
        let activeGummyType = null;
        let gummyTime = 0;
        
        if (gummyData.ruby_gummy > 0) {
          activeGummyType = 'ruby';
          gummyTime = gummyData.ruby_gummy;
        } else if (gummyData.sapphire_gummy > 0) {
          activeGummyType = 'sapphire';
          gummyTime = gummyData.sapphire_gummy;
        } else if (gummyData.emerald_gummy > 0) {
          activeGummyType = 'emerald';
          gummyTime = gummyData.emerald_gummy;
        } else if (gummyData.golden_gummy > 0) {
          activeGummyType = 'golden';
          gummyTime = gummyData.golden_gummy;
        } else if (gummyData.diamond_gummy > 0) {
          activeGummyType = 'diamond';
          gummyTime = gummyData.diamond_gummy;
        }
        
        setActiveGummy(activeGummyType);
        setGummyCountdown(gummyTime);
        
        // Notify parent component of active gummy change
        if (onActiveGummyChange) {
          onActiveGummyChange(activeGummyType);
        }
        
        // Check Ring of Health status
        const ringResponse = await api.get("/v1/ring-of-health-status", { withCredentials: true });
        setHasRingOfHealth(ringResponse.data.has_ring);
        setRingOnCooldown(ringResponse.data.on_cooldown);
        setRingCooldownRemaining(ringResponse.data.cooldown_remaining);
        
        // Note: Repel and Ring Potion status would need to be added to this endpoint
        // or fetched from a different endpoint if available
        // For now, keeping the state but not fetching (to avoid 404 errors)
      } catch (error) {
        console.error('Error checking lure status:', error);
      }
    };

    checkItemStatus();
    const interval = setInterval(checkItemStatus, 5000); // Check every 5 seconds for real-time updates
    return () => clearInterval(interval);
  }, []);

  // Handle heal party with Ring of Health
  const handleHealParty = async () => {
    if (ringOnCooldown || healingParty) return;
    
    setHealingParty(true);
    try {
      const response = await api.post("/v1/ring-of-health-heal", {}, { withCredentials: true });
      
      // Show success message with floating text
      if (spawnText && playerPos) {
        spawnText(
          `Party healed! ${response.data.healed_count} Pokemon restored to full HP.`,
          playerPos.x,
          playerPos.y - 20,
          '#4ade80' // green color
        );
      }
      
      // Update cooldown status
      setRingOnCooldown(true);
      setRingCooldownRemaining(300); // 5 minutes
    } catch (error) {
      console.error('Error healing party:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to heal party. Please try again.';
      
      // Show error message with floating text
      if (spawnText && playerPos) {
        spawnText(
          errorMsg,
          playerPos.x,
          playerPos.y - 20,
          '#f87171' // red color
        );
      }
    } finally {
      setHealingParty(false);
    }
  };

  // Countdown timers for repel and ring potion
  useEffect(() => {
    if (repelCountdown > 0) {
      const timer = setInterval(() => {
        setRepelCountdown(prev => {
          if (prev <= 1) {
            setRepelActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [repelCountdown]);

  useEffect(() => {
    if (ringPotionCountdown > 0) {
      const timer = setInterval(() => {
        setRingPotionCountdown(prev => {
          if (prev <= 1) {
            setRingPotionActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [ringPotionCountdown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Calculate total shiny odds - show as percentage and multiplier
  const calculateShinyOdds = () => {
    // Get the number of shiny charms equipped
    const charmCount = shinyCharm?.stack_amount || 0;
    
    // Base denominator is 4096, each charm doubles the odds (divides denominator by 2)
    const denom = Math.max(1, 4096 / Math.pow(2, charmCount));
    let basePct = (1 / denom) * 100;
    
    // Add skill tree bonus (additive percentage)
    const bonusPct = (shinySkillBonus || 0) * 100;
    let finalPct = basePct + bonusPct;
    
    // Shiny potion doubles the final odds
    if (shinyPotionActive) {
      finalPct *= 2;
    }
    
    // Calculate total multiplier for display
    let totalMultiplier = Math.pow(2, charmCount);
    if (shinySkillBonus > 0) {
      totalMultiplier *= (1 + shinySkillBonus / 100);
    }
    if (shinyPotionActive) {
      totalMultiplier *= 2;
    }
    
    return `Shiny odds: ${finalPct.toFixed(2)}%`;
  };

  const hasActiveItems = mineralPotionActive || shinyPotionActive || activeLure || repelActive || ringPotionActive || shinyCharm || shinySkillBonus > 0 || expLureHits > 0 || dropLureHits > 0 || scentgHits > 0 || activeGummy || hasRingOfHealth || equippedRings.length > 0;

  // Always show the card (even if no active items) so user can access bag
  // if (!hasActiveItems) return null;

  return (
    <div className={`bag-items-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="bag-items-header">
        <div onClick={() => setIsExpanded(!isExpanded)} style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: 'pointer' }}>
          <span className="bag-title">Active Effects</span>
          <span className="bag-toggle">{isExpanded ? '▼' : '▶'}</span>
        </div>
        {onOpenBag && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onOpenBag();
            }}
            style={{
              background: 'rgba(74, 108, 222, 0.8)',
              border: '1px solid rgba(74, 108, 222, 0.95)',
              padding: '6px',
              borderRadius: '6px',
              cursor: 'pointer',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img src={pouchIcon} alt="Open Bag" style={{ width: '18px', height: '18px' }} />
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="bag-items-content">
          {/* Shiny Odds Display - show if any bonus exists */}
          {(shinyCharm || shinyPotionActive || shinySkillBonus > 0) && (
            <div 
              className="bag-item shiny-odds"
              style={{
                background: 'rgba(0,0,0,0.6)',
                padding: '6px 8px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: '1px solid rgba(255, 215, 0, 0.3)'
              }}
            >
              {shinyCharm && (
                <img 
                  src="/items/shinycharm.png" 
                  alt="Shiny Charm" 
                  style={{ width: '20px', height: '20px', flexShrink: 0 }}
                />
              )}
              <span style={{ fontSize: '12px', color: '#fff', fontWeight: 600, flex: 1 }}>
                {calculateShinyOdds()}
              </span>
            </div>
          )}

          {/* Shiny Potion */}
          {shinyPotionActive && (
            <div className="bag-item active-potion">
              <img src="/items/ShinyPotion.png" alt="Shiny Potion" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-timer">{formatTime(shinyPotionCountdown)}</div>
              </div>
            </div>
          )}

          {/* Mineral Potion */}
          {mineralPotionActive && (
            <div className="bag-item active-potion">
              <img src="/items/MineralPotion.png" alt="Mineral Potion" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-timer">{formatTime(mineralPotionCountdown)}</div>
              </div>
            </div>
          )}

          {/* Active Lure */}
          {activeLure && (
            <div className="bag-item active-lure">
              <img 
                src={activeLure === 'lure' ? lureImg : activeLure === 'super_lure' ? superLureImg : ultraLureImg} 
                alt="Lure" 
                style={{ width: '20px', height: '20px', flexShrink: 0 }} 
              />
              <div className="item-info">
                <div className="item-timer">{formatTime(lureCountdown)}</div>
              </div>
            </div>
          )}

          {/* Repel */}
          {repelActive && (
            <div className="bag-item active-repel">
              <img src="/items/repel.png" alt="Repel" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-timer">{formatTime(repelCountdown)}</div>
              </div>
            </div>
          )}

          {/* Ring Potion */}
          {ringPotionActive && (
            <div className="bag-item active-ring">
              <img src="/items/ringpotion.png" alt="Ring Potion" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-timer">{formatTime(ringPotionCountdown)}</div>
              </div>
            </div>
          )}

          {/* Mining XP Lure */}
          {expLureHits > 0 && (
            <div className="bag-item active-lure">
              <img src={mineLureImg} alt="Mining Lure" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-value">{expLureHits} hits</div>
              </div>
            </div>
          )}

          {/* Drop Rate Lure */}
          {dropLureHits > 0 && (
            <div className="bag-item active-lure">
              <img src={dropLureImg} alt="Drop Lure" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-value">{dropLureHits} hits</div>
              </div>
            </div>
          )}

          {/* Scent of Gengar */}
          {scentgHits > 0 && (
            <div className="bag-item active-scent">
              <img src="/items/scentofgengar.png" alt="Scent of Gengar" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-value">{scentgHits} attempts</div>
              </div>
            </div>
          )}

          {/* Active Gummy */}
          {activeGummy && (
            <div className="bag-item active-gummy">
              <img 
                src={activeGummy === 'ruby' ? rubyGummyImg : 
                     activeGummy === 'sapphire' ? sapphireGummyImg : 
                     activeGummy === 'emerald' ? emeraldGummyImg : 
                     activeGummy === 'golden' ? goldenGummyImg : diamondGummyImg} 
                alt={`${activeGummy} Gummy`} 
                style={{ width: '20px', height: '20px', flexShrink: 0 }} 
              />
              <div className="item-info">
                <div className="item-value">{gummyCountdown} {gummyCountdown === 1 ? 'Battle' : 'Battles'}</div>
              </div>
            </div>
          )}

          {/* Ring of Health */}
          {hasRingOfHealth && (
            <div className="bag-item active-ring-health">
              <img src="/items/ringofhealth.png" alt="Ring of Health" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <button
                  onClick={handleHealParty}
                  disabled={ringOnCooldown || healingParty}
                  style={{
                    background: ringOnCooldown ? 'rgba(128, 128, 128, 0.5)' : 'rgba(76, 175, 80, 0.8)',
                    border: '1px solid rgba(76, 175, 80, 0.95)',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: ringOnCooldown || healingParty ? 'not-allowed' : 'pointer',
                    fontSize: '11px',
                    fontWeight: 600,
                    opacity: ringOnCooldown || healingParty ? 0.6 : 1
                  }}
                >
                  {healingParty ? 'Healing...' : ringOnCooldown ? formatTime(ringCooldownRemaining) : 'Heal Party'}
                </button>
              </div>
            </div>
          )}

          {/* Equipped Rings */}
          {equippedRings.map((ring) => {
            // Ring configurations
            const ringConfig = {
              28: { // Ring of Equipment
                image: '/items/ringofequipment.png',
                name: 'Ring of Equipment',
                action: onOpenHeldItemSwap,
                buttonText: 'Open',
                buttonColor: 'rgba(255, 193, 7, 0.8)',
                borderColor: 'rgba(255, 193, 7, 0.95)',
                bgColor: 'rgba(255, 193, 7, 0.15)',
                borderColorCSS: 'rgba(255, 193, 7, 0.3)'
              },
              140: { // Ring of Department
                image: '/items/ringofdepartment.png',
                name: 'Ring of Department',
                action: onOpenDepartmentStore,
                buttonText: 'Open',
                buttonColor: 'rgba(255, 215, 0, 0.8)',
                borderColor: 'rgba(255, 215, 0, 0.95)',
                bgColor: 'rgba(255, 215, 0, 0.15)',
                borderColorCSS: 'rgba(255, 215, 0, 0.3)'
              },
              161: { // Ring of Health (already handled separately above)
                skip: true
              }
            };
            
            const config = ringConfig[ring.item];
            if (!config || config.skip) return null;
            
            return (
              <div key={ring.item} className="bag-item" style={{ background: config.bgColor, borderColor: config.borderColorCSS }}>
                <img src={config.image} alt={config.name} style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                <div className="item-info">
                  <button
                    onClick={config.action}
                    style={{
                      background: config.buttonColor,
                      border: `1px solid ${config.borderColor}`,
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 600
                    }}
                  >
                    {config.buttonText}
                  </button>
                </div>
              </div>
            );
          })}

          {!hasActiveItems && (
            <div style={{ padding: '10px', textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
              No active items
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BagItemsCard;
